/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import moment from "dayjs";
import util from "../util/util.js";
import googleMaps from '../util/google-maps.js';
import markerLayer from '../map/remote-marker-layer.js';
import mapStyles from '../map/styles.js';
import areasMap from '../map/significant-areas.js';
import regionsMap from '../map/regions.js';
import * as Chartist from 'chartist';
import SortableTableViewModel from "./shared/sortable-table.js";
import DrawPolygonTool from "../util/draw-polygon-tool.js";
import programs from "../programs.js";
import createPagedSurveyList from "./shared/paged-survey-list.js";
import DensityMapType from '../map/density-map-type.js';

import "./shared/location-search.js";
import "./shared/searchable-select.js";
import "../util/knockout-pikaday.js";

var ExploreViewModel = function(params) {
	birdata.loadSpeciesSprite();
	document.body.classList.add('birdata-fullscreen');

	var self = this;

	self.loaded = ko.observable(false);

	self.iconSprite = require('open-iconic/sprite/open-iconic.min.svg');

	self.map = ko.observable();
	self.mapOptions = ko.observable();
	self.mapBounds = ko.observable();

	var monthOptions = util.monthsShort().map(function(m, i) {
		return {
			month: m,
			index: i + 1
		};
	});

	var drawPolygonTool = new DrawPolygonTool();

	self.regionFilter = ko.observable(null);
	self.programFilter = ko.observable(null);
	self.afterDateFilter = ko.observable(null);
	self.beforeDateFilter = ko.observable(null);
	self.visibleOnMapFilter = ko.observable(false);
	self.surveyTypeFilter = ko.observableArray();
	self.sopSurveyTypeFilter = ko.observable(null);
	self.speciesFilter = ko.observable(null);
	self.subspeciesFilter = ko.observable(null);
	self.guildFilter = ko.observableArray();
	self.classFilter = ko.observable(null);
	self.sourceFilter = ko.observable(null);
	self.userFilter = ko.observable(null);
	self.vettingStatusFilter = ko.observable(null);
	self.polygonFilter = ko.observable(null);

	self.gccFilter = ko.observable(false);

	self.areaLayerFilter = ko.observable(null);
	self.areaFilter = ko.observable(null);
	self.monthsFilter = ko.observableArray(monthOptions.map(m => m.index));
	self.allMonthsFilter = ko.pureComputed({
		read: function() {
			return self.monthsFilter().length === monthOptions.length;
		},
		write: function(value) {
			self.monthsFilter(value ? monthOptions.map(m => m.index) : []);
		}
	});
	self.monthsFilterDelayed = ko.pureComputed(function() {
		return self.monthsFilter();
	}).extend({ rateLimit: { timeout: 1500, method: "notifyWhenChangesStop"}});

	// Clear subspecies filter whenever species filter changes
	self.speciesFilter.subscribe(function() {
		self.subspeciesFilter(null);
	});

	self.statsLoading = ko.observable(false);
	self.statsLoaded = ko.observable(false);
	self.stats = ko.observable();

	self.showDetails = ko.observable(true);
	self.showVetting = ko.observable(false);

	self.showVetting.subscribe(function(show) {
		self.showDetails(!show);
	}); // Automatically hide details when vetting is shown

	self.showSpeciesStats = ko.pureComputed(function() {
		return self.showDetails() && !self.speciesFilter();
	});

	self.showStats = ko.pureComputed(function() {
		return self.showDetails() && !!self.speciesFilter();
	});

	self.filterCriteriaJSON = ko.pureComputed(function() {
		var criteria = {};

		if(self.afterDateFilter()) {
			criteria.from = moment(self.afterDateFilter()).format("YYYY-MM-DD");
		}

		if(self.beforeDateFilter()) {
			criteria.to = moment(self.beforeDateFilter()).format("YYYY-MM-DD");
		}

		if(self.regionFilter()) {
			criteria.region_id = self.regionFilter().id;
		}

		if(self.programFilter()) {
			criteria.program = self.programFilter().code;
		}

		if(self.visibleOnMapFilter()) {
			var bounds = self.mapBounds();
			if(bounds) {
				criteria.xmin = bounds.getSouthWest().lng();
				criteria.ymin = bounds.getSouthWest().lat();
				criteria.xmax = bounds.getNorthEast().lng();
				criteria.ymax = bounds.getNorthEast().lat();

				if(!self.userIsPrivileged()) {
					console.log("Desensitizing extent");
					criteria.xmin = Math.floor(criteria.xmin * 10) / 10;
					criteria.ymin = Math.floor(criteria.ymin * 10) / 10;
					criteria.xmax = Math.ceil(criteria.xmax * 10) / 10;
					criteria.ymax = Math.ceil(criteria.ymax * 10) / 10;
				}
			}
		}

		if(self.surveyTypeFilter().length > 0) {
			criteria.survey_type = self.surveyTypeFilter().map(x => x.code).join(",");
		}

		if(self.sopSurveyTypeFilter()) {
			criteria.sop_survey_type = self.sopSurveyTypeFilter().code;
		}

		if(self.areaFilter()) {
			criteria.area_id = self.areaFilter().id;
		} else if(self.areaLayerFilter()) {
			criteria.area_layer_id = self.areaLayerFilter().id;
		}

		if(self.monthsFilterDelayed()) {
			if(self.monthsFilterDelayed().length < 12) {
				criteria.months = self.monthsFilterDelayed().toSorted(util.compareNumber).join(",");
			}
		}

		if(self.speciesFilter()) {
			criteria.species_id = self.speciesFilter().id;
		}

		if(self.subspeciesFilter()) {
			criteria.taxon_id = self.subspeciesFilter().taxon_id;
		}

		if(self.sourceFilter()) {
			criteria.source = self.sourceFilter().code;
		}

		if(self.userFilter()) {
			criteria.user_id = self.userFilter().id;
		}

		if(self.vettingStatusFilter()) {
			criteria.vetting_status = self.vettingStatusFilter().code;
		}

		if(self.polygonFilter()) {
			criteria.polygon = drawPolygonTool.base64EncodedPolygon;
		}

		if(self.programFilter() && self.programFilter().code == 'wa_cockies' && self.gccFilter()) {
			criteria.wa_cockies_part_of_gcc = true;
		}

		if(self.programFilter() && self.programFilter().code == 'sop') {
			criteria.supertaxa = 'all';
		}

		criteria.permit_stale = true;

		// We are stringifying so that knockout can identify and suppress repeat values
		// (knockout does not deep-compare objects for equality)
		return JSON.stringify(criteria);
	});

	self.filterCriteria = ko.pureComputed(function() {
		return JSON.parse(self.filterCriteriaJSON());
	});

	//---- State handling -------
	var initialState = util.hashParams();
	var haveAppliedInitialState = false;

	function getState() {
		var state = { ...self.filterCriteria() };

		delete state.xmin;
		delete state.xmax;
		delete state.ymin;
		delete state.ymax;

		delete state.supertaxa;

		delete state.permit_stale;

		if(self.visibleOnMapFilter()) {
			state.visible_on_map = self.visibleOnMapFilter();
		}
		if(self.areaLayerFilter()) state.area_layer_id = self.areaLayerFilter().id;

		if(self.map()) {
			var map = self.map();
			self.mapBounds(); // trigger ko binding
			var center = map.getCenter();

			state.map = [center.lat().toFixed(7), center.lng().toFixed(7), map.getZoom()].join('_');
		}

		state = util.filterObject(state, ([k, v]) => !!v);

		return state;
	}

	function applyInitialState() {
		if(!initialState.area_id) {
			util.defer(function() { haveAppliedInitialState = true; }, 4);
		}

		if(initialState.species_id) {
			self.speciesFilter(self.species().find(s => s.id == +initialState.species_id));
		}
		if(initialState.taxon_id) {
			self.subspeciesFilter(self.species().find(s => s.taxon_id == initialState.taxon_id));
		}
		if(initialState.region_id) {
			self.regionFilter(self.regions().find(r => r.id == initialState.region_id));
		}
		if(initialState.program) {
			self.programFilter(self.programs().find(p => p.code == initialState.program));
		}
		if(initialState.visible_on_map) {
			self.visibleOnMapFilter(initialState.visible_on_map == "true");
		}
		if(initialState.area_layer_id) {
			self.areaLayerFilter(self.areaLayers().find(l => l.id == +initialState.area_layer_id));
		}
		if(initialState.area_id) {
			var areaInitialised = false;
			self.areas.subscribe(function(areas) {
				if(areas && areas.length && !areaInitialised) {
					self.areaFilter(areas.find(a => a.id == +initialState.area_id));
					areaInitialised = true;
					util.defer(function() { haveAppliedInitialState = true; }, 4);
				}
			});
		}
		if(initialState.polygon) {
			drawPolygonTool.base64EncodedPolygon = initialState.polygon;
		}
		if(initialState.from) {
			self.afterDateFilter(initialState.from);
		}
		if(initialState.to) {
			self.beforeDateFilter(initialState.to);
		}
		if(initialState.months) {
			var m = initialState.months.split(/[,_]/)
				.map(i => +i)
				.filter(i => !isNaN(i));
			self.monthsFilter(m);
		}
		if(initialState.source) {
			self.sourceFilter(self.sources().find(s => s.code == initialState.source));
		}
		if(initialState.user_id) {
			var userID = +initialState.user_id;

			self.userFilter({
				id: userID,
				full_name: "User " + userID
			});
			// Populate select with user's name:
			birdata.user(userID).then(function(user) {
				self.userFilter(user);
			});
		}
		if(initialState.vetting_status) {
			self.vettingStatusFilter(self.vettingStatuses().find(s => s.code == initialState.vetting_status));
		}

		if(initialState.survey_type) {
			let surveyTypeCodes = initialState.survey_type.split(",");
			self.surveyTypeFilter(
				self.surveyTypes().filter(st => surveyTypeCodes.includes(st.code))
			);
		}
		if(initialState.sop_survey_type) {
			self.sopSurveyTypeFilter(self.sopSurveyTypes().find(sst => sst.code == initialState.sop_survey_type));
		}

		if(initialState.program == 'wa_cockies' && initialState.wa_cockies_part_of_gcc) {
			self.gccFilter(initialState.gcc == 'true');
		}
	}

	ko.computed(function() {
		history.replaceState({}, '', '#' + util.encodeParams(getState()));
	});
	//---- End state handling ---

	function formattedNumber(obs, key) {
		return ko.pureComputed(function() {
			var o = obs();
			return o && o[key] !== undefined ? util.formatNumber(o[key]) : "";
		});
	}

	self.speciesImageURL = function(species) {
		return birdata.speciesImageURL(species.id, 'small_square');
	};

	self.surveyCount = formattedNumber(self.stats, 'survey_count');
	self.observerCount = formattedNumber(self.stats, 'observer_count');
	self.sharedSiteCount = formattedNumber(self.stats, 'shared_site_count');

	self.regions = ko.observable([]);
	self.programs = ko.observable([]);
	self.areaLayers = ko.observable([]);
	self.areas = ko.observable([]);
	self.surveyTypes = ko.observable([]);
	self.sopSurveyTypes = ko.observable([]);
	self.vettingStatuses = ko.observable([]);
	self.breedingActivities = ko.observable([]);
	self.months = monthOptions;
	self.species = ko.observable([]);
	self.selectableSpecies = ko.pureComputed(function() {
		if(self.programFilter() && self.programFilter().code == "sop") {
			return self.species();
		} else {
			return self.species().filter(s => s.supertaxa.includes("class:Aves"));
		}
	});
	self.selectableSubspecies = ko.pureComputed(function() {
		var s = self.speciesFilter();
		return s && s.subspecies || [];
	});
	self.showSubspeciesFilter = ko.pureComputed(function() {
		return false;
		// return self.selectableSubspecies().length > 1;
	});
	self.guilds = ko.observable([]);
	self.classes = ko.observable([]);
	self.speciesMap = {};
	self.sources = ko.observable([]);
	self.usersMatchingSearch = ko.observable([]);
	self.usersSearchText = ko.observable('');

	self.currentUser = ko.observable({});

	self.usersSearchText.subscribe(updateUsers);

	function updateUsers(search) {
		if(search) {
			birdata.users({q:search}).then(function(users) {
				if(self.usersSearchText() != search) return; // ignore stale request
				self.usersMatchingSearch(users);
			});
		} else {
			self.usersMatchingSearch([]);
		}
	}

	self.userIsVetter = ko.pureComputed(function() {
		var me = self.currentUser();
		// Determine if user is a vetter of any kind
		return me.is_administrator || me.permissions?.some(function(p) {
			return p.role == "vetter" || p.role == "admin";
		});
	});

	self.userIsPrivileged = ko.pureComputed(function() {
		var me = self.currentUser();

		var regionId = self.regionFilter() && self.regionFilter().id;
		var areaId = self.areaFilter() && self.areaFilter().id;
		var speciesId = self.speciesFilter() && self.speciesFilter().id;
		var programCode = self.programFilter() && self.programFilter().code;

		return me.is_administrator || me.permissions?.some(function(p) {
			return (p.role == "admin" && p.type == "region" && p.region_id == regionId) ||
				(p.role == "admin" && p.type == "area" && p.area_id == areaId) ||
				(p.role == "admin" && p.type == "program" && p.program == programCode && (p.area_id == null || p.area_id == areaId)) ||
				(p.role == "admin" && p.type == "species" && p.species_id == speciesId);
		});
	});
	self.userCanExport = self.userIsPrivileged;

	self.vettingAvailable = ko.pureComputed(function() {
		return self.userIsVetter() && !!self.speciesFilter();
	});

	ko.computed(function() {
		if(!self.vettingAvailable()) self.showVetting(false);
	});


	self.programFilter.subscribe(function(p) {
		if(haveAppliedInitialState) {
			// SOP: automatically select area layer when program selected
			if(p && p.code == 'sop' && self.areaLayers() && !self.areaLayerFilter()) {
				self.areaLayerFilter(self.areaLayers().find(l => l.code == 'sop' ));
			}
			// SOP: automatically clear class filter when program not selected
			if(!(p && p.code == 'sop')) {
				self.classFilter(null);
			}
		}
	});

	drawPolygonTool.polygon = self.polygonFilter;
	self.map.subscribe(function(map) {
		drawPolygonTool.map = map;
	});
	self.isDrawingPolygon = drawPolygonTool.isDrawingPolygon;
	self.drawPolygon = function() { drawPolygonTool.drawPolygon(); }
	self.cancelPolygon = function() { drawPolygonTool.cancelPolygon(); }

	Promise.all([
		birdata.lookup(),
		birdata.regions(),
		birdata.areaLayers(),
		birdata.speciesList({ supertaxa: "all" }),
		birdata.me(true).noLoginRedirect().catch(function() { return {}; }),
		programs()
	]).then(function([lookup, regions, areaLayers, speciesList, me, programLogic]) {
			var programs = lookup['program']


			// TODO: move this logic to somewhere more central
			self.programs(programs.filter(function(p) {
				if(p.code == 'sop') {
					return me.permissions?.some(p => p.program == 'sop');
				}
				if(p.code == 'powl') {
					return me.is_administrator || me.permissions?.some(p =>
						p.program == 'powl' && p.role == 'admin');
				}
				var pl = programLogic.find(p.code);
				if(pl && pl.canFilterBy) {
					return pl.canFilterBy(me);
				}
				return true;
			}));

			self.regions(regions);

			self.areaLayers(areaLayers.filter(function(layer) {
				// Only Admins can see RL containment layer
				if(layer.code == "rl_containment") {
					return me && (me.is_administrator || me.permissions?.some(p => p.role == "admin"));
				} else {
					return true;
				}
			}));

			// Breeding territory monitoring is often sensitive
			self.surveyTypes(lookup['survey_type'].filter(function(x) {
				return x.code != 'breeding_territory_monitoring' && x.code != '10_min_point_search' && x.code != 'acoustic_survey';
			}));

			self.sopSurveyTypes(lookup['sop_survey_type']);
			self.vettingStatuses(lookup['vetting_status']);
			self.breedingActivities(lookup['breeding_activity']);
			self.sources(lookup['source']);

			speciesList.forEach(function(s) {
				self.speciesMap[s.id] = s;
			});

			self.species(speciesList.filter(function(s) {
				return s.sensitivity != "category_1";
			}));

			var supertaxa = [...new Set(self.species().flatMap(sp => sp.supertaxa))]
				.map(x => {
					var parts = x.split(':', 2);
					return {
						rank: parts[0],
						name: parts[1]
					}
				});

			self.guilds(supertaxa.filter(x => x.rank == 'guild'));
			self.classes(supertaxa.filter(x => x.rank == 'class'));

			applyInitialState();

			self.currentUser(me);

			self.loaded(true);
		});

	self.areaLayerFilter.subscribe(function(areaLayer) {
		self.areaFilter(null);
		self.areas([]);

		if(areaLayer) {
			birdata.areas().then(function(areas) {
				var options = areas
					.filter(a => a.layer_id == areaLayer.id)
					.toSorted((a, b) => a.name.localeCompare(b.name));

				self.areas(options);
			});
		}
	});

	self.exportStarting = ko.observable(false);

	self.clickedExport = function() {
		self.exportStarting(true);

		var exportCriteria = { ...self.filterCriteria() };

		if(!self.speciesFilter() && self.programFilter() && self.programFilter().code == "sop") {
			if(self.classFilter()) {
				exportCriteria.supertaxa = "class:" + self.classFilter().name;
			} else {
				exportCriteria.supertaxa = "all";
			}
		}

		if(!self.speciesFilter() && self.guildFilter().length > 0) {
			exportCriteria.supertaxa = self.guildFilter().map(g => "guild:" + g.name).join(",");
		}


		exportCriteria.completed = "any";

		birdata.surveysExportURL(exportCriteria, 'csv').then(function(url) {
			document.location.href = url;
		}).finally(function() {
			self.exportStarting(false);
		});
	};

	googleMaps.load().then(function() {
		var center = new google.maps.LatLng(-22.50831, 136.078612);
		var zoom = 4;

		if(initialState.map) {
			var x = initialState.map.split("_");
			center = new google.maps.LatLng(+x[0], +x[1]);
			zoom = +x[2];
		}

		self.mapOptions(googleMaps.defaultOptions({
			center: center,
			zoom: zoom
		}));
	});

	var surveyPointLayer;
	self.filterCriteria.subscribe(function() {
		if(surveyPointLayer) {
			// Add a small delay so that other requests are initiated first.
			// Otherwise the map tile requests can block other more visually important requests
			setTimeout(function() {
				surveyPointLayer.refresh();
			}, 50);
		}
	});

	self.loadingTiles = ko.observable(0);
	self.visibleTiles = ko.observable(0);
	self.tilesInBatch = highWaterMark(self.loadingTiles);

	function highWaterMark(obs) {
		var prev = obs();
		var mark = obs();
		return ko.pureComputed(function() {
			var n = obs();
			if(n > prev) {
				mark = n;
			}
			prev = n;
			return mark;
		});
	}

	self.tileLoadingProgress = ko.pureComputed(function() {
		var x = self.loadingTiles();
		var n = self.tilesInBatch();

		// Note, if loading off-screen tiles due to fast map changes, progress could be
		// negative, so we set a minimum of 0.
		var percent = Math.max(0, Math.round(n == 0 ? 100 : (n - x) * 100 / n));

		return "Loading " + percent + "%";
	}).extend({ rateLimit: 50 });

	self.tilesLoaded = ko.pureComputed(function() {
		return self.loadingTiles() == 0;
	});

	var highlightedMarker;
	function highlightMarkerAtPoint(p) {
		if(!self.map()) return;
		unhighlightMarker();
		highlightedMarker = surveyPointLayer.getMarker(p.lat + "," + p.lon);
		if(highlightedMarker) {
			highlightedMarker.setIcon(mapStyles.surveyIcon(true));
			highlightedMarker.setZIndex(1000);
		}
	}
	function unhighlightMarker() {
		if(!self.map()) return;
		if(highlightedMarker) {
			highlightedMarker.setIcon(mapStyles.surveyIcon(false));
			highlightedMarker.setZIndex(1);
			highlightedMarker = null;
		}
	}

	function setupMap(map) {
		if(setupMap.called) return;
		setupMap.called = true;

		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.dragZoomControl(map));

		var highlightRecent = true;

		var overlay = new DensityMapType({
			tileSize: new google.maps.Size(256, 256),
			map: map,
			criteriaFn: self.filterCriteria,
			visibleTiles: self.visibleTiles,
			loadingTiles: self.loadingTiles,
			events: {
				mousemove: function(evt, info) {
					if(info.latLng) {
						hoverOverlay.position(info.latLng);
						// evt.stopPropagation();
					}
				},
				mouseover: function(evt, info) {
					if(info.latLng) {
						var count = evt.target.getAttribute('data-count');
						var recent = evt.target.getAttribute('data-recent') === "true";
						if(count) {
							hoverOverlay.position(info.latLng);

							var noun = self.filterCriteria().species_id ? "sighting" : "survey";
							var html = '<div>' + util.formatQuantity(+count, noun) + '</div>'

							if(highlightRecent) {
								var verb = self.filterCriteria().species_id ? "sighted" : "surveyed";
								if(recent) {
									html += '<div class="recent">' + util.capitalize(verb) + ' in the last 14 days</div>';
								} else {
									html += '<div class="not-recent">Not ' + verb + ' in the last 14 days</div>';
								}
							}

							hoverOverlay.html(html);
							hoverOverlay.show();
							// evt.stopPropagation();
						}
					}
				},
				mouseout: function(evt, info) {
					hoverOverlay.hide();
					// evt.stopPropagation();
				}
			},
			highlightRecent: highlightRecent
		});

		google.maps.event.addListener(map, 'zoom_changed', function() {
			hoverOverlay.hide();
		});

		self.filterCriteria.subscribe(function(){
			// Small delay to ensure other requests happen first.
			// Otherwise map tile loading can delay loading of more important UI elements
			setTimeout(function() {
				overlay.refresh();
			}, 10);
		});
		map.overlayMapTypes.insertAt(0, overlay);

		var recentDateThreshold = util.formatISODate(util.addDays(new Date(), -14));

		surveyPointLayer = markerLayer(map, {
			minZoom: 12,
			visibleTilesObs: self.visibleTiles,
			loadingTilesObs: self.loadingTiles,
			pointsForTile: function(tile) {
				return birdata.surveyTile(self.filterCriteria(), tile.x, tile.y, tile.z).then(function(points) {
					if(tile.removed) return;
					points.forEach(function(p) {
						// Populate ID needed by marker layer
						p.id = p.lat + "," + p.lon;
					});
					return points;
				});
			},
			markerForPoint: function(p) {
				var noun = self.speciesFilter() == null ? 'Survey' : 'Sighting';
				var title = util.formatQuantity(p.count, noun);

				if(p.approx) {
					title += ' (approximate location)';
				}

				var isRecent = p.last_surveyed > recentDateThreshold;
				var highlightRecent = true;

				var marker = new google.maps.Marker({
					position: new google.maps.LatLng(p.lat, p.lon),
					title: title,
					icon: mapStyles.surveyIcon(false, p.approx, highlightRecent && isRecent, p.abc),
					zIndex: isRecent ? 2 : 1
				});

				google.maps.event.addListener(marker, 'click', function() {
					showInfoWindow(p, marker, {disableAutoPan: self.visibleOnMapFilter() });
				});

				if(p.id == infoWindowMarkerId) {
					showInfoWindow(p, marker, { disableAutoPan: true });
				}

				return marker;
			}
		});

		var infoWindow;

		// Track which marker has an info window open so that we can re-show it if markers are reloaded
		let infoWindowMarkerId = null;

		// Dismiss info window by clicking anywhere outside
		google.maps.event.addListener(map, 'click', function() {
			if(infoWindow) {
				infoWindow.close();
				infoWindowMarkerId = null;
			}
		});

		function showInfoWindow(p, marker, infoWindowOptions = {}) {
			if(infoWindow) {
				infoWindow.close();
			}

			infoWindow = new google.maps.InfoWindow({
				content: infoWindowContent(p),
				...infoWindowOptions
			});
			infoWindow.open(map, marker);

			infoWindowMarkerId = p.id;
			google.maps.event.addListener(infoWindow, 'closeclick', function() {
				infoWindowMarkerId = null;
			});
		}

		function infoWindowContent(p) {
			let title = self.speciesFilter() == null ? 'Surveys' : 'Sightings';
			return createPagedSurveyList(self.filterCriteria(), p, title);
		}

		function hoverContentPromise(poly) {
			switch(poly.userData.layerID) {
				case "areas":
					return Promise.all([
						birdata.area(poly.userData.id),
						areasMap.significanceDescription(poly.userData.id)
					]).then(([area, description]) => {
						if(area) {
							return area.name + (description ? "<hr>" + description : "");
						} else {
							return null;
						}
					});
			}
		}

		var hoverOverlay = googleMaps.hoverOverlay(map);
		var polygonEvents = {
			mousemove: function(evt) {
				hoverOverlay.position(evt.latLng);
			},
			mouseover: function(evt) {
				var featureID = hoverOverlay.featureID = this.userData.id;
				var layerID = hoverOverlay.layerID = this.userData.layerID;
				hoverOverlay.position(evt.latLng);

				hoverContentPromise(this).then(function(content) {
					if(hoverOverlay.featureID != featureID || hoverOverlay.layerID != layerID) return; // ignore stale

					hoverOverlay.html(content);
					hoverOverlay.show();
				});
			},
			mouseout: function(evt) {
				hoverOverlay.featureID = hoverOverlay.layerID = null;
				hoverOverlay.hide();
			},
		};

		var areaMapLayer = areasMap.showLayer(map, {
			layerID: "areas",
			polygonEvents: polygonEvents
		});

		ko.computed(function() {
			var areaLayer = self.areaLayerFilter();
			var area = self.areaFilter();
			areaMapLayer.setFeatureFilter(function(feature) {
				var attrs = feature.userData && feature.userData.attributes;
				if(!attrs) return true; //Needed?
				if(!areaLayer) return false;
				if(!area) return areaLayer.id == attrs.layer_id;
				else return area.id == attrs.id;
			});
		});

		var regionMapLayer = regionsMap.showLayer(map, {
			layerID: "regions"
		});

		ko.computed(function() {
			var region = self.regionFilter();
			regionMapLayer.setFeatureFilter(function(feature) {
				var attrs = feature.userData && feature.userData.attributes;
				if(!attrs) return true; //Needed?
				if(!region) return false;
				return region.id == attrs.id;
			});
		});

		google.maps.event.addListener(map, 'idle', function() {
			self.mapBounds(map.getBounds());
		});
		self.mapBounds(map.getBounds());

		// Auto zoom to area/region
		ko.computed(function() {
			var area = self.areaFilter();
			var areaLayer = self.areaLayerFilter();
			var region = self.regionFilter();

			if(initialState.map && !haveAppliedInitialState) return;

			var bounds;
			if(area && area.bounds) bounds = googleMaps.bounds(area.bounds, bounds);
			// Only zoom to entire area layer for SOP
			if(!area && areaLayer && areaLayer.code == 'sop') bounds = googleMaps.bounds(areaLayer.bounds, bounds);
			if(region) bounds = googleMaps.bounds(region.bounds, bounds);
			if(bounds) map.fitBounds(bounds);
		});

		setupVettingToggle(map);
		setupDetailsToggle(map);
	}

	function setupVettingToggle(map) {
		var button = document.createElement('div');
		button.className = 'map-toggle-button vetting bottom';
		button.textContent = 'Vet sightings';

		var state = self.showVetting;

		button.addEventListener('click', function() { state(!state()); });

		ko.computed(function() {
			util.toggleClass(button, 'selected', state());
			setTimeout(function() { google.maps.event.trigger(map, 'resize'); });
		});

		ko.computed(function() {
			button.style.display = self.vettingAvailable() ? 'block' : 'none';
		});

		map.controls[google.maps.ControlPosition.BOTTOM_LEFT].push(button);
	}

	function setupDetailsToggle(map) {
		var button = document.createElement('div');
		button.className = 'map-toggle-button right';
		button.textContent = 'Show details';

		var state = self.showDetails;

		button.addEventListener('click', function() { state(!state()); });

		ko.computed(function() {
			util.toggleClass(button, 'selected', state());
			button.innerText = state() ? 'Hide details' : 'Show details';
			setTimeout(function() { google.maps.event.trigger(map, 'resize'); });
		});

		map.controls[google.maps.ControlPosition.RIGHT_TOP].push(button);
	}

	ko.computed(function() {
		var map = self.map();
		if(map) setupMap(map);
	});

	self.locationResultSelected = function(suggestion) {
		var data = suggestion.data;
		var map = self.map();

		if(!map) {
			return;
		}

		switch(suggestion.type) {
			case 'LOCATION':
				map.fitBounds(new google.maps.LatLngBounds(
				  new google.maps.LatLng(data.ymin, data.xmin),
				  new google.maps.LatLng(data.ymax, data.xmax)));
				break;
			case 'SHARED_SITE':
				map.fitBounds(googleMaps.bounds(data.bounds));
				break;
		}
	};
	self.sharedSitesPromise = birdata.sharedSites(); // Used for location search

	// -- species stats

	self.speciesStatsLoaded = ko.observable(false);
	self.speciesStatsLoading = ko.observable(false);
	self.speciesStats = ko.observable([]);

	self.showSortMenu = ko.observable(false);
	self.sortOptions = [{
		label: "Sort by taxonomy",
		key: function(s) { return s.species.sort; }
	}, {
		label: "Sort by common name",
		key: function(s) { return s.species.common_name; }
	}, {
		label: "Sort by scientific name",
		key: function(s) { return s.species.scientific_name; }
	}, {
		label: "Sort by reporting rate",
		key: function(s) { return -s.rawCount; }
	}];
	self.selectedSortOption = ko.observable(self.sortOptions[0]);

	self.speciesCount = ko.pureComputed(function() {
		if(self.speciesStatsLoaded()) {
			return "(" + util.formatNumber(self.speciesStats().length) + ")";
		} else {
			return "";
		}
	});

	ko.computed(function() {
		if(self.loaded() && self.filterCriteriaJSON() && self.showSpeciesStats()) {
			updateSpeciesStats(self.filterCriteriaJSON());
		}
	});

	function updateSpeciesStats(criteriaJSON) {
		var criteria = JSON.parse(criteriaJSON);

		if(criteria.species_id) {
			console.log("Unexpected call to updateSpeciesStats when filtering on a species - suppressing request");
			return;
		}

		self.speciesStatsLoaded(false);
		self.speciesStatsLoading(true);

		Promise.all([
			birdata.surveyStats(criteria, 'surveys_by_species'),
			birdata.surveyStats(criteria, 'basic_stats')
		]).then(function([speciesStats, surveyStats]) {
				if(criteriaJSON != self.filterCriteriaJSON()) return; // ignore stale

				var surveyCount = surveyStats.survey_count;
				var stats = speciesStats.map(function(stat) {
					return {
						species: self.speciesMap[stat.species_id],
						count: util.formatNumber(stat.count),
						rawCount: stat.count,
						reportingRate: surveyCount == 0 ? '--' : (stat.count * 100 / surveyCount).toFixed(2) + "%"
					};
				});

				self.speciesStats(stats);

				self.speciesStatsLoaded(true);
			}).finally(function() {
				self.speciesStatsLoading(false);
			});
	}

	self.sortedSpeciesStats = ko.pureComputed(function() {
		let key = self.selectedSortOption().key;
		return util.sortBy(self.speciesStats(), key);
	});

	self.printSpecies = function() {
		var w = window.open();
		w.document.body.innerHTML = require("../../templates/print/species-stats-list.html");
		w.ko = ko;
		ko.applyBindings({
			sortedSpeciesStats: self.sortedSpeciesStats
		}, w.document.body);
		w.print();
	}

	self.canDownloadSpecies = function() {
		// https://github.com/Modernizr/Modernizr/blob/master/feature-detects/a/download.js
		return !window.externalHost && 'download' in document.createElement('a');
		// return false;
	};

	self.downloadSpecies = function() {
		var csv = self.sortedSpeciesStats().map(function(s) {
			return [
				s.species.common_name,
				s.species.scientific_name,
				s.rawCount,
				s.reportingRate
			].join(",");
		}).join("\n");

		csv = "Common Name,Scientific Name,Count,Reporting Rate\n" + csv;

		util.downloadString(csv, 'species.csv', 'text/csv');
	}

	// --- stats & charts

	var statsCriteria = {};

	ko.computed(function() {
		if(self.filterCriteriaJSON() && self.showStats()) {
			updateStats(self.filterCriteriaJSON());
		} else {
			updateStats("");
		}
	});

	function updateStats(criteriaJSON) {
		if(criteriaJSON == "") {
			statsCriteria.next = statsCriteria.now = "";
			return; // Nothing to actually do
		}

		statsCriteria.next = criteriaJSON; // This is what we want to load
		if(statsCriteria.now == statsCriteria.next) return; // If it's already loaded, nothing to do
		if(statsCriteria.loading) return; // If we are already loading, do nothing for now

		var criteria = JSON.parse(criteriaJSON);

		if(!criteria.species_id) {
			console.log("Unexpected call to updateStats when not filtering on a species - suppressing request");
			return;
		}

		self.statsLoading(true);
		self.statsLoaded(false);

		statsCriteria.loading = true; // OK let's load stuff
		Promise.all([
			birdata.surveyStats(criteria, 'basic_stats'),
			birdata.surveyStats(criteria, 'surveys_by_month'),
			birdata.surveyStats(criteria, 'surveys_by_year'),
			birdata.surveyStats(criteria, 'reporting_rate_by_year')
		]).then(
			function([surveyStats, surveysByMonth, surveysByYear, reportingRateByYear]) {
				statsCriteria.loading = null; // We're done loading now
				if(statsCriteria.next != criteriaJSON) { // But now there is something else to load
					updateStats(statsCriteria.next); // Load it then
					return;
				}
				statsCriteria.now = criteriaJSON; // OK we have what we wanted

				// if(criteriaJSON != self.filterCriteriaJSON()) {
				// 	console.log("Ignoring stale request: " + criteriaJSON + " vs " + self.filterCriteriaJSON());
				// 	return; // Ignore stale requests
				// }
				self.stats(surveyStats);

				if(surveyStats.survey_count > 0) {
					updateCharts(surveysByMonth, surveysByYear, reportingRateByYear);
				}
				self.statsLoaded(true);
			}).finally(function() {
				statsCriteria.loading = null; // We're done loading now
				self.statsLoading(false);
			});
	}

	// Used for CSV download, populated by updateCharts()
	var chartData = {};

	self.downloadSurveysByMonthCSV = function() {
		downloadCSV(chartData.surveysByMonth, 'surveys-vs-month.csv', ['month', 'count'], ['Month', 'Surveys'])
	}

	self.downloadSurveysByYearCSV = function() {
		downloadCSV(util.sortBy(chartData.surveysByYear, x => x.year), 'surveys-vs-year.csv', ['year', 'count'], ['Year', 'Surveys'])
	}

	self.downloadReportingRateCSV = function() {
		downloadCSV(chartData.reportingRateByYear, 'reporting-rate.csv', ['year', 'reporting_rate'], ['Year', 'Reporting Rate'])
	}

	function downloadCSV(data, filename, keys, columns) {
		var csv = data.map(function(row) {
			return keys.map(function(key) {
				return row[key];
			}).join(",");
		}).join("\n");

		csv = [columns].join(",") + "\n" + csv;

		util.downloadString(csv, filename, 'text/csv');
	}

	function updateCharts(surveysByMonth, surveysByYear, reportingRateByYear) {
		chartData = {
			surveysByMonth, surveysByYear, reportingRateByYear
		};

		var lineOptions = {
			axisY: {
				onlyInteger: true,
				labelInterpolationFnc: function(value, index) {
					return util.formatNumber(value);
				}
			},
			axisX: {
				type: Chartist.AutoScaleAxis,
				scaleMinSpace: 30,
				onlyInteger: true
			},
			lineSmooth: Chartist.Interpolation.monotoneCubic(),
			showLine: true,
			showPoint: true
		};

		var rrOptions = {
			axisY: {
				onlyInteger: false,
				labelInterpolationFnc: function(value, index) {
					return util.formatNumber(value);
				},
				low: 0
			},
			axisX: {
				type: Chartist.AutoScaleAxis,
				scaleMinSpace: 30,
				onlyInteger: true
			},
			lineSmooth: Chartist.Interpolation.monotoneCubic(),
			showLine: true,
			showPoint: true
		};

		// If there is only one year, show as a single point and set Y axis range manually
		if(surveysByYear.length == 1) {
			lineOptions.showPoint = true;
			lineOptions.axisY.high = surveysByYear[0].count + 1;
			lineOptions.axisY.low = 0;
		}

		var barOptions = {
			axisY: {
				onlyInteger: true,
				labelInterpolationFnc: function(value, index) {
					return util.formatNumber(value);
				}
			},
			axisX: {
				labelInterpolationFnc: function(value, index) {
					return util.monthsShort()[value];
				}
			}
		};

		// Limit data to 1998 and onwards
		surveysByYear = surveysByYear.filter(function(x) { return x.year >= 1998 });
		reportingRateByYear = reportingRateByYear.filter(function(x) { return x.year >= 1998 });
		// Set axis range from 1998 to present
		lineOptions.axisX.low = 1998;
		lineOptions.axisX.high = (new Date()).getFullYear();
		rrOptions.axisX.low = 1998;
		rrOptions.axisX.high = (new Date()).getFullYear();

		setTimeout(function() {
			new Chartist.LineChart('.surveys-vs-year', surveysByYearChartData(surveysByYear), lineOptions);
			new Chartist.BarChart('.surveys-vs-month', surveysByMonthChartData(surveysByMonth), barOptions);
			new Chartist.LineChart('.reporting-rate-vs-year', reportingRateByYearChartData(reportingRateByYear), rrOptions);
		});
	}

	function sparseToDense(sparse, xKey, yKey, minX, maxX, defaultY) {
		var xs = Array(maxX - minX + 1).map(function() { return defaultY; });
		sparse.forEach(function(p) {
			var x = p[xKey];
			var y = p[yKey];

			if(x >= minX && x <= maxX) {
				xs[x - minX] = y;
			}
		});
		return xs;
	}

	function reportingRateByYearChartData(reportingRateByYear) {
		var series = reportingRateByYear.map(function(p) {
			return {
				x: p.year,
				y: p.reporting_rate
			}
		});

		// split into contiguous sections
		var currentSeries;
		var allSeries = [];
		var lastX;
		series.forEach(function(p) {
			if(p.x === lastX + 1) {
				currentSeries.push(p);
			} else {
				if(currentSeries) {
					allSeries.push(currentSeries);
				}
				currentSeries = [p];
			}
			lastX = p.x;
		});
		if(currentSeries) {
			allSeries.push(currentSeries);
		}

		return {
			series: allSeries
		};
	}

	function surveysByYearChartData(surveysByYear, allSurveysByYear) {
		var years = surveysByYear.map(x => x.year);
		var minYear = Math.min(...years);
		var maxYear = Math.max(...years);

		var surveysSeries = sparseToDense(surveysByYear, 'year', 'count', minYear, maxYear, 0).map(function(count, i) {
			return {
				x: minYear + i,
				y: count
			}
		});

		return {
			series: [ surveysSeries ]
		};
	}


	function surveysByMonthChartData(surveysByMonth) {
		var values = [];
		var labels = [];
		for(var i = 0; i < 12; i++) {
			values[i] = 0;
			labels[i] = i;
		}
		surveysByMonth.forEach(function(p) {
			values[p.month - 1] = p.count;
		});

		return {
			labels: labels,
			series: [ values ]
		};
	}

	self.speciesMatchFn = function(option, text, search) {
		text = text.toLowerCase().replace("-", " ");
		search = search.toLowerCase().replace("-", " ");
		return text.indexOf(search);
	};

	// ------ Vetting

	self.vettableSightingsLoaded = ko.observable(false);
	self.vettableSightingsLoading = ko.observable(false);
	self.vettableSightings = ko.observable([]);
	self.moreSightings = ko.observable(false);

	var vettingLoadTimer;
	ko.computed(function() {
		if(self.loaded() && self.filterCriteriaJSON() && self.showVetting() && self.filterCriteria().species_id) {
			if(vettingLoadTimer) {
				clearTimeout(vettingLoadTimer);
			}
			vettingLoadTimer = setTimeout(function() { updateVettableSightings(self.filterCriteriaJSON()); }, 100);
		}
	});

	function updateVettableSightings(criteriaJSON) {
		console.log('update vettable sightings!');
		var criteria = JSON.parse(criteriaJSON);

		self.vettableSightingsLoaded(false);
		self.vettableSightingsLoading(true);
		self.vettableSightings([]);
		self.moreSightings(false);

		birdata.vettableSightingsLegacy(criteria).then(function(sightings) {
			if(criteriaJSON != self.filterCriteriaJSON()) return; // ignore stale

			if(sightings.length > 100) {
				sightings = sightings.slice(0, 100);
				self.moreSightings(true);
			} else {
				self.moreSightings(false);
			}

			var sightingModels = sightings.map(sightingModel);
			self.vettableSightings(sightingModels);

			self.vettableSightingsLoaded(true);
		}).finally(function() {
			self.vettableSightingsLoading(false);
		});
	}

	function sightingModel(s) {
		var props = {
			vettingNotes: ko.observable(''),
			newStatus: ko.observable(null),
			state: ko.observable('idle'), // idle, editing, saving, error
			data: ko.observable(s),
			edit: function() {
				props.state('editing');
			},
			save: function() {
				props.state('saving');
				var review = {
					notes: props.vettingNotes(),
					vetting_status: props.newStatus().code
				};
				birdata.submitVettingReview(s.sighting.id, review).then(function() {
					props.newStatus(null); // this has to go first before setting state, not sure why
					props.state('idle');
					return birdata.sighting(s.sighting.id);
				}).then(function(sighting) {
					props.data(sighting);
				}).catch(function(error) {
					// Show error message for a few seconds
					props.state('error');
					setTimeout(function() { props.state('idle'); }, 4000);
					console.log(error);
				});
			},
			cancel: function() {
				props.state('idle');
				props.newStatus(null);
			},
			showFields: ko.pureComputed(function() { return props.state() != 'idle'; }),
			fieldsEnabled: ko.pureComputed(function() { return props.state() == 'editing'; }),
			saving: ko.pureComputed(function() { return props.state() == 'saving'; }),
			error: ko.pureComputed(function() { return props.state() == 'error'; })
		};

		props.newStatus.subscribe(function(c) {
			if(c) {
				props.state('editing');
			} else {
				props.state('idle');
			}
		});

		return props;
	}

	self.mouseOverSighting = function(s) {
		highlightMarkerAtPoint(s.data().survey.survey_point);
	};

	self.mouseOutSighting = function(s) {
		unhighlightMarker();
	};

	self.clickSighting = function(s, evt) {
		if(evt.target.closest('.col-vetting_update, a, button')) {
			return true;
		} else {
			// Open survey summary in new window
			window.open('survey?id=' + s.data().survey.id).focus();
		}
	}

	self.vettingTable = new SortableTableViewModel([
		{
			name: 'Survey point',
			value: function(s) {
				return s.data().survey.survey_point.name;
			},
			formatted: function(s) {
				return this.value(s) || "(Unnamed)";
			},
			id: 'name'
		}, {
			name: 'Observer',
			value: function(s) {
				return s.data().user_name;
			},
			formatted: function(s) {
				return this.value(s);
			},
			id: 'user'
		}, {
			name: 'Date',
			value: function(s) {
				return s.data().survey.start_date;
			},
			formatted: function(s) {
				var val = this.value(s);
				return val ? moment(val).format("DD/MM/YYYY") : "–";
			},
			id: 'start_date'
		}, {
			name: 'Survey type',
			value: function(s) {
				var type = self.surveyTypes().find(st => st.code == s.data().survey.survey_type);
				if(type) return type.name;
			},
			formatted: function(s) {
				return this.value(s);
			},
			id: 'survey_type'
		}, {
			name: 'Accuracy',
			value: function(s) {
				return s.data().survey.survey_point.accuracy_in_metres;
			},
			formatted: function(s) {
				var x = this.value(s);

				if(x === undefined) {
					return "–";
				} else if(x <= 0) {
					return "Within survey area";
				} else if(x <= 1000) {
					return "Within " + util.formatDistance(x);
				} else {
					return "> 1km";
				}
			},
			id: "accuracy"
		}, {
			name: 'Count',
			value: function(s) {
				return s.data().sighting.count;
			},
			formatted: function(s) {
				var count = this.value(s);
				return count ? util.formatNumber(count) : 'P';
			},
			id: 'count'
		}, {
			name: 'Breeding',
			value: function(s) {
				var x = self.breedingActivities().find(b => b.code == s.data().sighting.breeding_activity);
				return x ? x.name : "–";
			},
			formatted: function(s) {
				return this.value(s);
			},
			id: 'breeding_activity'
		}, {
			name: 'Notes',
			value: function(s) {
				var notes = s.data().sighting.notes || "–";
				if(s.data().survey.notes) {
					notes += " (Survey notes: " + s.data().survey.notes + ")";
				}
				return notes;
			},
			html: function(s) {
				var notes = this.value(s);
				var maxChars = 60;

				var mediaHtml = "";

				var media = s.data().sighting.media_uuids || [];
				if(media.length > 0) {
					mediaHtml = media.map(function(uuid) {
						return "<br><a href='" + birdata.uploadURL(uuid) + "' target='_blank'>Attachment</a>";
					}).join("");
				}

				if(notes.length > maxChars) {
					return [
						util.escapeHtml(notes.substr(0, maxChars)) + "&hellip;",
						mediaHtml,
						"<div class='help'>",
						"<span class='icon'></span>",
						"<span class='text right top' style='white-space: pre-wrap;'>" + util.escapeHtml(notes) + "</span>",
						"</div>"
					].join(" ");
				} else {
					return util.escapeHtml(notes) + mediaHtml;
				}
			},
			id: 'notes',
			click: function(data, evt) {
				// Allow default click action so media attachment links can be opened
				return true;
			}
		}, {
			name: 'Vetting status',
			value: function(s) {
				var status = self.vettingStatuses().find(v => v.code == s.data().sighting.vetting_status);
				if(status) return status.name;
			},
			formatted: function(s) {
				return this.value(s);
			},
			id: 'vetting_status'
		}, {
			name: 'Update',
			value: function(s) {
				return '';
			},
			formatted: function(s) {
				return this.value(s);
			},
			id: 'vetting_update',
			custom: true,
			sortable: false
		}
	]);
	self.vettingTable.sortDescriptors([{
		column: 'name',
		ascending: true
	}]);
};

ko.components.register('explore', {
	viewModel: function(params) {
		return new ExploreViewModel(params);
	},
	template: require('../../templates/explore.html')
});

export default ExploreViewModel;
